import { HttpService, QueryHandler, RootContextBase, TaggedUrl, Tags, transformDTODate } from '@skylab-shared/core';
import { AddressDTO, ClientKind, SalePatternKsefSendOption } from '@skylab-shared/dataModel';
import { Transform, Type } from 'class-transformer';
import { PagedCollectionQuery, PagedCollectionQueryResult, PagedCollectionQueryResultDTO } from '../../common/query/paged-collection-query';
import { CountryCollectionQuery } from '../../country/queries/country.query-base';
import { CurrencyCollectionQuery, CurrencyItem } from '../../currency/queries/currency.query-base';

export interface SalePatternGridItem {
    version: string;
    id: number;
    externalId: string;

    active: boolean;
    automated: boolean;
    client: string;
    clientKind: ClientKind;
    clientCountry: string;
    clientAddress: string;
    currency: CurrencyItem;
    favourite: boolean;
    grossValue: number;
    lastIssuedDocumentNumber: string;
    lastIssuedDocumentId: number;
    name: string;
    note: string | null;
    nextIssueDate: Date | null;
    sendDocumentToClient: boolean;
    ksefSendOption: SalePatternKsefSendOption
}

class SalePatternCollectionItemDTO {
    public readonly id!: number;
    public readonly externalId!: string;
    public readonly version!: string;
    public readonly active!: boolean;
    public readonly automated!: boolean;
    public readonly client!: string;
    public readonly clientKind!: ClientKind;
    @Type(() => AddressDTO) public readonly clientAddress!: AddressDTO;
    public readonly currencyId!: number;
    public readonly favourite!: boolean;
    public readonly grossValue!: number;
    public readonly issueDay!: number;
    public readonly lastIssuedDocumentNumber!: string;
    public readonly lastIssuedDocumentId!: number;
    public readonly name!: string;
    public readonly note: string | null;
    @Transform(transformDTODate, { toClassOnly: true }) public readonly nextIssueDate!: Date;
    public readonly sendDocumentToClient!: boolean;
    public readonly ksefSendOption!: SalePatternKsefSendOption;
}

export class SalePatternGridQuery extends PagedCollectionQuery<SalePatternGridItem> { }

export class SalePatternGridQueryHandlerBase implements QueryHandler<SalePatternGridQuery, PagedCollectionQueryResult<SalePatternGridItem>> {
    public constructor(
        private readonly http: HttpService,
        private readonly rootContext: RootContextBase
    ) { }

    public async handleAsync(query: SalePatternGridQuery): Promise<PagedCollectionQueryResult<SalePatternGridItem>> {
        const url = TaggedUrl.join('documentTemplates', [Tags.Api]);
        const { data } = await this.http.get(url, {
            params: { ...query.params },
            dtoObject: new PagedCollectionQueryResultDTO<SalePatternCollectionItemDTO>(SalePatternCollectionItemDTO)
        });

        const items = await Promise.all(data.items.map<Promise<SalePatternGridItem>>(async item => {
            const itemCurrency = await this.rootContext.firstAsync(CurrencyCollectionQuery, { id: item.currencyId });
            const country = await this.rootContext.firstAsync(CountryCollectionQuery, {id: item.clientAddress?.countryId});

            return {
                version: item.version,
                id: item.id,
                externalId: item.externalId,

                active: item.active,
                automated: item.automated,
                client: item.client,
                clientAddress: item.clientAddress ? [item.clientAddress.address, item.clientAddress.city].filter(Boolean).join(', ') : '',
                clientCountry: country?.name || '',
                clientKind: item.clientKind,
                currency: itemCurrency!,
                favourite: item.favourite,
                grossValue: item.grossValue,
                lastIssuedDocumentNumber: item.lastIssuedDocumentNumber,
                lastIssuedDocumentId: item.lastIssuedDocumentId,
                name: item.name,
                note: item.note,
                nextIssueDate: item.nextIssueDate,
                sendDocumentToClient: item.sendDocumentToClient,
                ksefSendOption: item.ksefSendOption
            };
        }));

        return {
            items: items,
            totalItemsCount: data.totalItemsCount
        };
    }
}
